import { inject, Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';

import { StorageService } from './storage-service';

export interface AuthUser {
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly storageService = inject(StorageService);

  login() {
    globalThis.location.href = `${environment.apiUrl}/api/v1/saml/login`;
  }

  handleToken(token: string) {
    this.storageService.setLocal('api-token', token);

    const payload = this.parseJwt(token);

    if (!payload) {
      throw new Error('Invalid JWT token');
    }

    const user = {
      email: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],

      firstName: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'],

      lastName: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'],

      fullName: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
    };

    this.storageService.setLocal('user', user);
  }

  isLoggedIn(): boolean {
    const token = this.storageService.getLocal<string>('api-token');

    if (!token) {
      return false;
    }

    const payload = this.parseJwt(token);

    if (!payload) {
      return false;
    }

    const exp = +payload['exp'];

    if (!exp) {
      return false;
    }

    const expiryDate = new Date(exp * 1000);

    const isExpired = expiryDate < new Date();

    return !isExpired;
  }

  getToken(): string | null {
    return this.storageService.getLocal('api-token');
  }

  getUser(): AuthUser | null {
    return this.storageService.getLocal<AuthUser>('user');
  }

  private parseJwt(token: string): Record<string, string> | undefined {
    try {
      const base64Url = token.split('.')[1];

      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );

      return JSON.parse(jsonPayload) as Record<string, string>;
    } catch {
      return undefined;
    }
  }
}
